import { BaseService } from "../../core/baseService";
import {
  PromotionServiceInterface,
  Promotion,
  PromotionUsage,
  PromotionTypeOption,
  GetPromotionsParams,
  CreatePromotionParams,
  UpdatePromotionParams,
  DeletePromotionParams,
  TogglePromotionStatusParams,
  GetPromotionUsagesParams,
} from "../types/promotion.types";
import { ApiResult, PaginatedResponse } from "../../core/types";

export class PromotionService
  extends BaseService
  implements PromotionServiceInterface
{
  async getPromotions(
    params?: GetPromotionsParams,
  ): Promise<ApiResult<PaginatedResponse<Promotion>>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.pageSize)
      queryParams.append("pageSize", params.pageSize.toString());
    if (params?.searchTerm) queryParams.append("searchTerm", params.searchTerm);
    if (params?.promotionType)
      queryParams.append("promotionType", params.promotionType);
    if (params?.isActive !== undefined)
      queryParams.append("isActive", params.isActive.toString());

    const queryString = queryParams.toString();
    const url = queryString
      ? `/api/admin/promotions?${queryString}`
      : "/api/admin/promotions";

    return this.handleRequest<PaginatedResponse<Promotion>>(
      this.client.get(url),
    );
  }

  async getAllPromotions(
    includeInactive = false,
  ): Promise<ApiResult<Promotion[]>> {
    return this.handleRequest<Promotion[]>(
      this.client.get(
        `/api/admin/promotions/all?includeInactive=${includeInactive}`,
      ),
    );
  }

  async getPromotion(promotionId: string): Promise<ApiResult<Promotion>> {
    return this.handleRequest<Promotion>(
      this.client.get(`/api/admin/promotions/${promotionId}`),
    );
  }

  async createPromotion(
    params: CreatePromotionParams,
  ): Promise<ApiResult<Promotion>> {
    return this.handleRequest<Promotion>(
      this.client.post("/api/admin/promotions", params),
    );
  }

  async updatePromotion(params: UpdatePromotionParams): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.put(
        `/api/admin/promotions/${params.promotionId}`,
        params.data,
      ),
    );
  }

  async deletePromotion(params: DeletePromotionParams): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.delete(`/api/admin/promotions/${params.promotionId}`),
    );
  }

  async togglePromotionStatus(
    params: TogglePromotionStatusParams,
  ): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.patch(`/api/admin/promotions/${params.promotionId}/status`, {
        isActive: params.isActive,
      }),
    );
  }

  async getPromotionUsages(
    params: GetPromotionUsagesParams,
  ): Promise<ApiResult<PromotionUsage[]>> {
    const queryParams = params.limit ? `?limit=${params.limit}` : "";
    return this.handleRequest<PromotionUsage[]>(
      this.client.get(
        `/api/admin/promotions/${params.promotionId}/usages${queryParams}`,
      ),
    );
  }

  async getPromotionTypes(): Promise<ApiResult<PromotionTypeOption[]>> {
    return this.handleRequest<PromotionTypeOption[]>(
      this.client.get("/api/admin/promotions/types"),
    );
  }
}
